import { call, put, select, takeLatest, takeEvery } from 'typed-redux-saga'
import { errorHandler, modalsSlice, notifyMessageCreator, loadersSlice } from 'Modules'
import { RequestParams, ResponseCatalog } from 'Models'
import {
	contractsRegistryApi,
	ContractsRegistryNS,
	contractsRegistrySelectors,
	contractsRegistrySlice,
	PartnerContract,
	TContractMeta,
} from 'Pages/pp'
import { MODAL_NAME } from 'Constants/ModalName'
import { FormNames } from 'Constants/FormNames'
import { downloadFile } from 'Helpers'

function* getList(action: ContractsRegistryNS.GetList) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { params, clear, callback } = action.payload
		let paramsForRequest
		if (params) {
			paramsForRequest = params
		} else {
			paramsForRequest = yield* select(contractsRegistrySelectors.selectRequestParams)
		}
		const res = yield* call(contractsRegistryApi.getAll, paramsForRequest)
		const contracts = new ResponseCatalog<PartnerContract, TContractMeta>(res)
		if (!clear && paramsForRequest?.page !== 1) {
			const oldContracts = yield* select(contractsRegistrySelectors.selectItems)
			contracts.entries = [...oldContracts.entries, ...contracts.entries]
		}
		yield* put(contractsRegistrySlice.actions.setList(contracts))
		yield* put(contractsRegistrySlice.actions.setRequestParams(paramsForRequest))
		callback?.()
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(contractsRegistrySlice.actions.setIsLoading(false))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* createContract(action: ContractsRegistryNS.CreateContract) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(contractsRegistryApi.create, action.payload)
		const params = yield* select(contractsRegistrySelectors.selectRequestParams)
		const { per_page, query, ...fields } = params
		yield* put(
			contractsRegistrySlice.actions.getList({
				params: new RequestParams(1, per_page, query, fields),
			})
		)
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.EDIT_CONTRACTS_REGISTRY))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'contracts_registry.create.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error, FormNames.PP.CONTRACT_REGISTRY_EDIT)
	}
}
function* deleteContract(action: ContractsRegistryNS.DeleteContract) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(contractsRegistryApi.delete, action.payload)
		const params = yield* select(contractsRegistrySelectors.selectRequestParams)
		const { per_page, query, ...fields } = params
		yield* put(
			contractsRegistrySlice.actions.getList({
				params: new RequestParams(1, per_page, query, fields),
			})
		)
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.REMOVE_CONTRACT_REGISTRY))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'contracts_registry.delete.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}
function* updateContract(action: ContractsRegistryNS.UpdateContract) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { data, id, modalForClose, set_status, callback } = action.payload
		yield* call(contractsRegistryApi.update, data, id)
		if (set_status)
			yield* call(contractsRegistryApi.set_status_partner, data?.partner_id, set_status)
		const params = yield* select(contractsRegistrySelectors.selectRequestParams)
		const { per_page, query, ...fields } = params
		yield* put(
			contractsRegistrySlice.actions.getList({
				params: new RequestParams(1, per_page, query, fields),
			})
		)
		if (modalForClose) yield* put(modalsSlice.actions.hideModal(modalForClose))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'contracts_registry.update.success')
		callback?.()
	} catch (error) {
		const { modalForClose } = action.payload
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(
			errorHandler,
			error,
			modalForClose === MODAL_NAME.EDIT_CONTRACTS_REGISTRY
				? FormNames.PP.CONTRACT_REGISTRY_EDIT
				: undefined
		)
	}
}

function* massUpdateContract(action: ContractsRegistryNS.MassUpdateContract) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { data, params } = action.payload
		yield* call(contractsRegistryApi.massUpdate, data, params)
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.EDIT_CONTRACTS_REGISTRY))
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'contracts_registry.mass_update.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error, FormNames.PP.CONTRACT_REGISTRY_EDIT)
	}
}

function* massUpdateContractFinished(action: ContractsRegistryNS.MassUpdateContractFinished) {
	try {
		yield* call(notifyMessageCreator, 'contracts_registry.mass_update.success_finished')
		const { ids } = action.payload
		const oldData = yield* select(contractsRegistrySelectors.selectItems)
		const itemsIds = oldData.entries?.map(({ id }) => id)
		if (ids.some((id) => itemsIds?.includes(id))) {
			const newData = yield* call(
				contractsRegistryApi.getAll,
				new RequestParams(1, 1000, null, { id: ids })
			)
			yield* put(
				contractsRegistrySlice.actions.setList({
					...oldData,
					entries: oldData.entries.map((contract) => {
						const findedIdx = newData.entries.findIndex(({ id }) => contract.id === id)
						return findedIdx !== -1 ? newData.entries[findedIdx] : contract
					}),
				})
			)
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getFaces(action: ContractsRegistryNS.GetFaces) {
	try {
		const faces = yield* call(contractsRegistryApi.getFaces, action.payload)
		yield* put(contractsRegistrySlice.actions.setFaces(faces))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* createAstPartnerRequest(action: ContractsRegistryNS.createAstPartnerRequest) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { partner_id, contract_id, callback } = action.payload
		yield* call(contractsRegistryApi.createAstPartnerRequest, { partner_id, contract_id })
		yield* call(notifyMessageCreator, 'ast_partner_request.create.success')
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		const { modalForClose } = action.payload
		if (modalForClose) yield* put(modalsSlice.actions.hideModal(modalForClose))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* limitRemainder(action: ContractsRegistryNS.GetLimitReminder) {
	try {
		yield* call(contractsRegistryApi.limitRemainder, action.payload)
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

function* downloadLimitDocument(action: ContractsRegistryNS.DownloadLimitDocument) {
	const { id } = action.payload
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(contractsRegistryApi.downloadLimitDocument, id)
		const url = window.URL.createObjectURL(res)
		downloadFile(url, 'limit_notification')
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'common.document_upload.success')
		window.URL.revokeObjectURL(url)
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export function* contractsRegistrySaga() {
	yield* takeLatest(contractsRegistrySlice.actions.getList, getList)
	yield* takeLatest(contractsRegistrySlice.actions.createContract, createContract)
	yield* takeLatest(contractsRegistrySlice.actions.deleteContract, deleteContract)
	yield* takeLatest(contractsRegistrySlice.actions.updateContract, updateContract)
	yield* takeLatest(contractsRegistrySlice.actions.massUpdateContract, massUpdateContract)
	yield* takeEvery(
		contractsRegistrySlice.actions.massUpdateContractFinished,
		massUpdateContractFinished
	)
	yield* takeLatest(contractsRegistrySlice.actions.getFaces, getFaces)
	yield* takeLatest(contractsRegistrySlice.actions.createAstPartnerRequest, createAstPartnerRequest)
	yield* takeLatest(contractsRegistrySlice.actions.getLimitReminder, limitRemainder)
	yield* takeLatest(contractsRegistrySlice.actions.downloadDocument, downloadLimitDocument)
}
